import { Country } from '@/src/types/apps/resto365Countries'
import authConfig from 'src/configs/auth'
import { setSessionLocale } from './intl'
import oktaAuth from '@/src/services/okta'
import { AccessToken } from '@okta/okta-auth-js'
import { persistor } from '@/src/store'

let sessionCountry: Country = Country.AUS
let isRefreshingAccessToken = false
let refreshAccessTokenPromise: Promise<string> = Promise.resolve('')

const getCountryFromStr = (country: string | null) => {
  const val = (country || '').toString().trim().toUpperCase()

  // Map common aliases
  if (val === 'US' || val === 'USA' || val === 'UNITED_STATES' || val === Country.USA) {
    return Country.USA
  }
  if (val === 'AU' || val === 'AUS' || val === 'AUSTRALIA' || val === Country.AUS) {
    return Country.AUS
  }

  // Fallback to existing default behaviour (AUS)
  return Country.AUS
}

const setSessionCountry = (c: Country) => {
  const country = getCountryFromStr(c)
  localStorage.setItem(authConfig.userCountryKeyName, country)
  sessionCountry = country
  setSessionLocale(country)
}

const setSessionCountryFromStorage = () => {
  const country: Country = getCountryFromStr(localStorage.getItem(authConfig.userCountryKeyName))
  sessionCountry = country
  setSessionLocale(country)
}

const clearSession = async () => {
  sessionCountry = Country.AUS

  if (localStorage.getItem(authConfig.storageTokenKeyName)) {
    // await oktaAuth.signOut()
    // Revokes tokens without killing the session and logging out the user from all okta apps
    // https://github.com/okta/okta-auth-js/issues/1429
    // - haritha
    await oktaAuth.revokeRefreshToken()
    await oktaAuth.revokeAccessToken()
    localStorage.removeItem(authConfig.userCountryKeyName)
    localStorage.removeItem(authConfig.storageTokenKeyName)
  }
  persistor.pause()
  persistor.flush().then(() => {
    return persistor.purge()
  })
}

const getOrRefreshAccessToken = async () => {
  const isAuthenticated = await oktaAuth.isAuthenticated()
  const token = isAuthenticated ? oktaAuth.getAccessToken() : await refreshAccessToken()

  return token ?? ''
}

const refreshAccessToken = async () => {
  // refresh access token in progress, simply return the current operation
  if (isRefreshingAccessToken) {
    return refreshAccessTokenPromise
  }
  try {
    isRefreshingAccessToken = true

    // try to refresh the access token
    refreshAccessTokenPromise = new Promise<string>(async (resolve, reject) => {
      try {
        const token = (await oktaAuth.tokenManager.renew('accessToken')) as AccessToken | null
        if (token) {
          resolve(token.accessToken)
        } else {
          // if no access token received, reject
          reject()
        }
      } catch (error) {
        reject()
      }
    })

    return await refreshAccessTokenPromise
  } catch {
    // return the current session token if refresh failed,
    // the server will decide if the session is still valid, if not throw a 401
    // this will log out the user
    // - haritha
    return ''
  } finally {
    // refreshing complete, reset the flag
    isRefreshingAccessToken = false
  }
}

export {
  setSessionCountry,
  setSessionCountryFromStorage,
  sessionCountry,
  clearSession,
  getOrRefreshAccessToken,
  getCountryFromStr
}
